import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../commons/store/hooks';
import { RootState } from '../../commons/reducers/rootReducer';
import { loadRepairCosts, openRepairCostDialog } from './repairCostDucks';
import { InvoiceStatusLabels, RebillableLabelButtons, RebillingTypeLabels, RepairCost } from './type';
import helpers from '../../commons/helpers/helpers';

type RepairCostListProps = {
  repairId?: string | null;
  canAddRepairCost: boolean;
};

const formatCost = (cost: number | null) =>
  cost != null ? cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €' : '—';

const RepairCostList = ({ repairId, canAddRepairCost }: RepairCostListProps) => {
  const dispatch = useAppDispatch();
  const repairCosts: RepairCost[] = useSelector((state: RootState) => state.repairCosts.costs);

  useEffect(() => {
    if (repairId) {
      dispatch(loadRepairCosts(repairId));
    }
  }, [dispatch, repairId]);

  if (!repairId) return null;

  return (
    <div className="repair-cost-list">
      <div className="repair-cost-title">
        <label>Coûts des réparations</label>
        {canAddRepairCost && (
          <button
            className="btn-icon add"
            style={{ cursor: 'pointer' }}
            onClick={() => dispatch(openRepairCostDialog(repairId))}
            type="button"
          />
        )}
      </div>
      {repairCosts.length > 0 && (
        <table className="repair-cost-table">
          <tbody>
            <tr>
              <th>Devis</th>
              <th>Commande</th>
              <th>Facture</th>
              <th>Refacturation</th>
            </tr>
            {repairCosts.map((cost) => (
              <tr
                key={cost.id}
                className={`${cost.rebillable === 'PENDING' ? 'pending-rebillable' : ''}`}
                onClick={(_) => dispatch(openRepairCostDialog(repairId, cost))}
              >
                <td>
                  {cost.quote ? (
                    <>
                      N° {cost.quote.number}
                      <br />
                      {helpers.dateTimeFormat(cost.quote.date, 'D MMM YYYY')}
                      <br />({formatCost(cost.quote.cost)})
                    </>
                  ) : null}
                </td>
                <td>
                  {cost.order ? (
                    <>
                      N° {cost.order.number}
                      <br />
                      {helpers.dateTimeFormat(cost.order.date, 'D MMM YYYY')}
                      <br />({formatCost(cost.order.cost)})
                    </>
                  ) : null}
                </td>
                <td>
                  {cost.invoice ? (
                    <>
                      <span>{InvoiceStatusLabels[cost.invoice.status!]}</span>
                      N° {cost.invoice.number}
                      <br />
                      {helpers.dateTimeFormat(cost.invoice.date, 'D MMM YYYY')}
                      <br />({formatCost(cost.invoice.cost)})
                    </>
                  ) : null}
                </td>
                <td>
                  {cost.rebillable && cost.rebillable !== 'YES' ? (
                    <span>{RebillableLabelButtons[cost.rebillable].label}</span>
                  ) : null}
                  {cost.rebilling ? (
                    <>
                      N° {cost.rebilling.number}
                      <br />
                      {helpers.dateTimeFormat(cost.rebilling.date, 'D MMM YYYY')}
                      <br />
                      {RebillingTypeLabels[cost.rebilling?.type]}
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RepairCostList;
